import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@volca/ui/lib/toast";
import type {
  ApAgentAction,
  ApBulkApproveResponse,
} from "@/accounts-payable/types";
import { useApi } from "@/core/api";
import { getErrorMessage } from "@/core/api/errors";
import { agentRunsKey, invoicesKey, receiptsKey, reviewQueueKey } from "./keys";

const approveBody = (vars: {
  payload?: unknown;
  acknowledgedBasisDigest?: string;
}) => {
  const body = {
    ...(vars.payload !== undefined && { payload: vars.payload }),
    ...(vars.acknowledgedBasisDigest && {
      acknowledged_basis_digest: vars.acknowledgedBasisDigest,
    }),
  };
  return Object.keys(body).length > 0 ? body : undefined;
};

export const useResolveAgentAction = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      actionId: string;
      decision: "approved" | "rejected";
      payload?: unknown;
      /** The `basis_digest` of a 409 the reviewer acknowledged. */
      acknowledgedBasisDigest?: string;
    }) =>
      api
        .post<ApAgentAction>(
          `/businesses/${businessId}/ap/agent_actions/${vars.actionId}/${
            vars.decision === "approved" ? "approve" : "reject"
          }`,
          vars.decision === "approved" ? approveBody(vars) : undefined,
        )
        .then((response) => response.data),
    // A refused approve re-read ServiceTitan and kept what changed on the
    // action, so the rows can say it.
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({ queryKey: invoicesKey(businessId) });
      void queryClient.invalidateQueries({ queryKey: receiptsKey(businessId) });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};

/** Ids per bulk approve request (see useBulkApproveAgentActions). */
const BULK_APPROVE_CHUNK = 5;

/** A bulk approve that stopped part way: what earlier requests approved stands. */
class BulkApproveStoppedError extends Error {
  constructor(
    readonly approved: number,
    readonly notApproved: number,
    readonly reason: unknown,
  ) {
    super("Bulk approve stopped part way");
  }
}

/**
 * Approve several agent actions at once.
 * POST /businesses/:id/ap/agent_actions/bulk_approve  ·  Body: { ids: [...] }
 *
 * The server skips any id that isn't pending_review rather than failing the
 * batch, and enqueues an apply → ServiceTitan per approved action, so we
 * invalidate the same caches as the single-action resolve. Actions that a
 * re-check didn't clear (ServiceTitan changed, would refuse them now, or
 * couldn't be reached) are left out and listed as `skipped`, to be reviewed
 * one by one; this says how many, and why when there is one.
 *
 * Each id costs the server a ServiceTitan re-read, so ids go a few at a time,
 * keeping every request well inside the proxy's timeout, and the results are
 * combined. A failure toasts what went through, so callers needn't.
 */
export const useBulkApproveAgentActions = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const combined: ApBulkApproveResponse = { actions: [], skipped: [] };
      for (let start = 0; start < ids.length; start += BULK_APPROVE_CHUNK) {
        try {
          const { data } = await api.post<ApBulkApproveResponse>(
            `/businesses/${businessId}/ap/agent_actions/bulk_approve`,
            { ids: ids.slice(start, start + BULK_APPROVE_CHUNK) },
          );
          combined.actions.push(...data.actions);
          combined.skipped.push(...data.skipped);
        } catch (error) {
          // Ids this request carried may have gone through before it failed;
          // retrying is harmless, since the server leaves out settled ones.
          throw new BulkApproveStoppedError(
            combined.actions.length,
            combined.skipped.length + ids.length - start,
            error,
          );
        }
      }
      return combined;
    },
    onError: (error) => {
      const stopped = error instanceof BulkApproveStoppedError ? error : null;
      const description = `${getErrorMessage(stopped ? stopped.reason : error)} Try again for the rest.`;
      if (stopped && stopped.approved > 0) {
        toast.error(
          `Approved ${stopped.approved}. ${stopped.notApproved} may still need approving`,
          { description },
        );
      } else {
        toast.error("Couldn't approve those actions", { description });
      }
    },
    onSuccess: ({ actions, skipped }) => {
      if (skipped.length > 0) {
        toast.info(
          `Approved ${actions.length}. ${skipped.length} left for review`,
          {
            description:
              skipped.length === 1
                ? `${skipped[0].reason.replace(/\.$/, "")}. Review it on its own.`
                : "ServiceTitan changed, would refuse them now, or couldn't be checked. Review them one by one.",
          },
        );
      }
    },
    // Earlier chunks may have gone through when a later one fails.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({ queryKey: invoicesKey(businessId) });
      void queryClient.invalidateQueries({ queryKey: receiptsKey(businessId) });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};

export const useBulkRejectAgentActions = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api
        .post<
          ApAgentAction[]
        >(`/businesses/${businessId}/ap/agent_actions/bulk_reject`, { ids })
        .then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({ queryKey: invoicesKey(businessId) });
      void queryClient.invalidateQueries({ queryKey: receiptsKey(businessId) });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};

export const useUpdateAgentAction = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: (vars: { actionId: string; payload: any }) =>
      api
        .patch<ApAgentAction>(
          `/businesses/${businessId}/ap/agent_actions/${vars.actionId}`,
          { payload: vars.payload },
        )
        .then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};
